<!-- =========================================================================================
    File Name: Chat.vue
    Description: Chat Application - Stay connected
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div id="chat-app" class="border border-solid d-theme-border-grey-light rounded relative overflow-hidden">
		<vs-sidebar
			class="items-no-padding"
			parent="#chat-app"
			:click-not-close="clickNotClose"
			:hidden-background="clickNotClose"
			v-model="isChatSidebarActive"
			id="chat-list-sidebar"
		>
			<!-- USER PROFILE SIDEBAR -->
			<user-profile
				:active="activeProfileSidebar"
				:userId="userProfileId"
				class="user-profile-container"
				:isLoggedInUser="isLoggedInUserProfileView"
				@closeProfileSidebar="closeProfileSidebar"
			></user-profile>

			<div class="chat__profile-search flex p-4">
				<div class="relative inline-flex">
					<feather-icon
						@click="showProfileSidebar(Number(activeUser.uid), true)"
						icon="MenuIcon"
						class="m-0 cursor-pointer"
					/>
				</div>
				<vs-input
					icon-no-border
					icon="icon-search"
					icon-pack="feather"
					class="w-full mx-5 input-rounded-full"
					:placeholder="activeTab === 0 ? $t('Chat.SearchOrStartANewChat') : $t('Chat.SearchContact')"
					v-model="searchQuery"
				/>
				<feather-icon
					class="md:inline-flex lg:hidden -ml-3 cursor-pointer"
					icon="XIcon"
					@click="toggleChatSidebar(false)"
				/>
			</div>

			<vs-divider class="d-theme-border-grey-light m-0" />

			<vs-tabs v-model="activeTab" class="m-4">
				<vs-tab icon-pack="feather" icon="icon-message-circle" label="Chats">
					<div class="mt-3">
						<vue-perfect-scrollbar class="scroll-area" :settings="settings" @ps-scroll-y="scrollHandle">
							<!-- ACTIVE CHATS LIST -->
							<div class="chat__chats-list mb-8">
								<ul class="chat__active-chats bordered-items">
									<li
										class="cursor-pointer"
										v-for="(chat, index) in chats"
										:key="index"
										@click="updateActiveChat(chat)"
									>
										<chat-contact
											showLastMsg
											:contact="chat"
											:unseenMsg="chatUnseenMessages(8)"
											:isActiveChatUser="isActiveChatUser(chat.key)"
											:userInfoId="userInfoId"
										></chat-contact>
									</li>
								</ul>
							</div>
						</vue-perfect-scrollbar>
					</div>
				</vs-tab>
				<vs-tab icon-pack="feather" icon="icon-user" :label="$t('Chat.Contacts')">
					<div class="mt-3">
						<vue-perfect-scrollbar class="scroll-area" :settings="settings" @ps-scroll-y="scrollHandle">
							<!-- CONTACTS LIST -->
							<div class="chat__contacts">
								<ul class="chat__contacts bordered-items">
									<li
										class="cursor-pointer"
										v-for="contact in contacts"
										:key="contact.id"
										@click="updateActiveChatUser(contact)"
									>
										<chat-contact :contact="contact"></chat-contact>
									</li>
								</ul>
							</div>
						</vue-perfect-scrollbar>
					</div>
				</vs-tab>
			</vs-tabs>
		</vs-sidebar>

		<!-- RIGHT COLUMN -->
		<div
			class="
				chat__bg
				no-scroll-content
				chat-content-area
				border border-solid
				d-theme-border-grey-light
				border-t-0 border-r-0 border-b-0
			"
			:class="{
				'sidebar-spacer--wide': clickNotClose,
				'flex items-center justify-center': activeChatUser === null || !activeChatUser,
			}"
		>
			<template v-if="activeChatUser">
				<div class="chat__navbar">
					<chat-navbar
						:isSidebarCollapsed="!clickNotClose"
						:user-id="activeChatUser"
						:isGroup="isGroup"
						:activeChatRoom="activeChatRoom"
						:userInfoId="userInfoId"
						:groupName="groupName"
						:isPinnedProp="isChatPinned"
						@openSettingsSidebar="openSettingsSidebar"
						@showProfileSidebar="showProfileSidebar"
						@toggleIsChatPinned="toggleIsChatPinned"
						@deleteChatEmit="openConfirmDelete"
					></chat-navbar>
				</div>
				<component
					:is="scrollbarTag"
					class="chat-content-scroll-area border border-solid d-theme-border-grey-light"
					:settings="settings"
					ref="chatLogPS"
					:key="$vs.rtl"
				>
					<div class="chat__log" ref="chatLog">
						<chat-log :chatKey="activeChatKey" v-if="activeChatUser"></chat-log>
					</div>
				</component>
				<div class="chat__input flex p-4 bg-white">
					<vs-input
						class="flex-1"
						:placeholder="$t('Chat.TypeYourMessage')"
						v-model="typedMessage"
						@keyup.enter="sendMsg"
					/>
					<chat-image class="ml-2" @imageUploadedEmit="senImageMessage" :chatKey="activeChatKey"></chat-image>

					<vs-dropdown vs-trigger-click class="cursor-pointer">
						<feather-icon icon="SmileIcon" class="ml-4 cursor-pointer" svgClasses="h-6 w-6"></feather-icon>
						<vs-dropdown-menu class="w-custom">
							<div class="flex flex-wrap items-center justify-start m-2">
								<span
									v-for="(emoji, index) in emojis"
									:key="index"
									@click="() => (typedMessage += emoji)"
									class="emoji cursor-pointer m-1"
									><p>{{ emoji }}</p></span
								>
							</div>
						</vs-dropdown-menu>
					</vs-dropdown>

					<vs-button class="bg-primary-gradient ml-4" type="filled" @click="sendMsg">{{
						$t("Submit")
					}}</vs-button>
				</div>
			</template>
			<template v-else>
				<div class="flex flex-col items-center justify-center">
					<feather-icon
						icon="MessageSquareIcon"
						class="mb-4 bg-white p-8 shadow-md rounded-full"
						svgClasses="w-16 h-16"
					></feather-icon>
					<h4
						class="py-2 px-4 bg-white shadow-md rounded-full cursor-pointer"
						@click.stop="toggleChatSidebar(true)"
					>
						{{ $t("Chat.StartConversation") }}
					</h4>
				</div>
			</template>
		</div>

		<chat-settings
			:isSettingsSidebarActive="isSettingsSidebarActive"
			:userInfoId="userInfoId"
			:members="groupMembers"
			:chat="activeChatRoom"
			@closeSettingsSidebarEmit="isSettingsSidebarActive = false"
			@removeMemberEmit="removeMemberFromList"
			@updateMembersEmit="updateMembersEmit"
		/>
	</div>
</template>

<script>
import ChatContact from "./ChatContact.vue";
import ChatLog from "./ChatLog.vue";
import ChatNavbar from "./ChatNavbar.vue";
import UserProfile from "./UserProfile.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import {firebaseChatDB} from "../../main";
import Chat from "@/models/Chat.js";
import ChatSettings from "./ChatSettings.vue";
import emojis from "./emojis";
import ChatImage from "./ChatImage.vue";

export default {
	data() {
		return {
			active: true,
			isHidden: false,
			searchContact: "",
			activeProfileSidebar: false,
			activeChatUser: 0,
			userProfileId: -1,
			typedMessage: "",
			isChatPinned: false,
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.7,
			},
			clickNotClose: true,
			isChatSidebarActive: true,
			isLoggedInUserProfileView: false,
			firebaseChatDB: firebaseChatDB,
			chatInfo: {
				chats: [],
				users: [],
			},
			activeChatKey: "",
			activeTab: 0,
			isGroup: false,
			groupName: "",
			activeChatRoom: new Chat({}),
			isSettingsSidebarActive: false,
			groupMembers: [],
			emojis: emojis,
		};
	},
	computed: {
		chatUnseenMessages() {
			return (userId) => {
				const unseenMsg = this.$store.getters["chat/chatUnseenMessages"](userId);
				if (unseenMsg) return unseenMsg;
			};
		},
		activeUser() {
			return this.$store.state.AppActiveUser;
		},
		chats() {
			return this.$store.getters["chat/chats"];
		},
		contacts() {
			return this.$store.getters["chat/contacts"];
		},
		searchQuery: {
			get() {
				return this.$store.state.chat.chatSearchQuery;
			},
			set(val) {
				this.$store.dispatch("chat/setChatSearchQuery", val);
			},
		},
		scrollbarTag() {
			return this.$store.getters.scrollbarTag;
		},
		isActiveChatUser() {
			return (chatKey) => chatKey === this.activeChatKey;
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		userInfoId() {
			const userInfo = JSON.parse(localStorage.getItem("userInfo"));
			return userInfo.datarutas_id;
		},
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		openSettingsSidebar(val) {
			this.isSettingsSidebarActive = true;
		},
		scrollHandle(evt) {},
		lastMessage() {
			const chat = this.$store.getters["chat/chatDataOfUser"](this.activeChatKey);
			const num = Object.keys(chat.messages)
				.map(Number)
				.sort((a, b) => b - a);
			return num[0];
		},
		getUserStatus(isActiveUser) {
			return isActiveUser ? this.$store.state.AppActiveUser.status : this.contacts[this.activeChatUser].status;
		},
		closeProfileSidebar(value) {
			this.activeProfileSidebar = value;
		},
		async updateActiveChat(chat) {
			this.activeChatRoom = new Chat(chat);
			if (this.activeChatRoom.admin || this.isDefaultChat(this.activeChatRoom.key)) {
				this.isGroup = true;
				this.groupName = chat.name;
				this.groupMembers = await this.$store.dispatch("chat/fetchContacts", {id: chat.members});
			} else {
				this.isGroup = false;
				this.groupMembers = [];
			}
			this.activeChatKey = chat.key || "";
			this.activeChatUser = chat.members.filter((member) => member !== this.userInfoId)[0];
			this.typedMessage = "";
			if (!this.activeChatRoom.admin) {
				this.$store.dispatch("chat/markSeenAllMessages", this.activeChatRoom);
			} else {
				this.$store.dispatch("chat/markSeenAllGroupMessages", this.activeChatRoom);
			}
		},
		updateActiveChatUser(contact) {
			this.activeChatUser = contact.id;
			this.activeChatKey = contact.chatKey || "";
			if (this.activeTab === 1) {
				const userInfo = JSON.parse(localStorage.getItem("userInfo"));
				const chats = this.$store.getters["chat/chats"];
				const filterChats = chats.filter((c) => c.members.includes(userInfo.datarutas_id));
				const foundChat = filterChats.find((c) => c.members.includes(+contact.id) && !c.admin);

				if (foundChat && foundChat.members.length <= 2 && Object.hasOwnProperty.call(foundChat, "key")) {
					this.activeTab = 0;
					setTimeout(() => {
						this.activeChatKey = foundChat.key.toString();
					}, 500);
				}
			}

			const chatData = this.$store.getters["chat/chatDataOfUser"](this.activeChatUser);

			if (chatData) this.isChatPinned = chatData.isPinned;
			else this.isChatPinned = false;
			this.toggleChatSidebar();
			this.typedMessage = "";
		},
		showProfileSidebar(userId, openOnLeft = false) {
			this.userProfileId = userId;
			this.isLoggedInUserProfileView = openOnLeft;
			this.activeProfileSidebar = !this.activeProfileSidebar;
		},
		sendMsg() {
			if (!this.typedMessage) return;
			if (this.activeChatKey === "") {
				this.createChatRoom();
			} else {
				this.updateChatRoom();
			}
		},
		updateChatRoom() {
			this.$store.dispatch("chat/sendChatMessage", {
				receiver: this.activeChatRoom.admin
					? this.activeChatRoom.members.filter((member) => member !== this.userInfoId)
					: [this.activeChatUser],
				text: this.typedMessage,
				chatKey: this.activeChatKey,
				is_group: this.activeChatRoom.admin ? true : false,
			});
			this.typedMessage = "";
			const scroll_el = this.$refs.chatLogPS.$el || this.$refs.chatLogPS;
			scroll_el.scrollTop = this.$refs.chatLog.scrollHeight;
		},
		async createChatRoom() {
			const chatKey = await this.$store.dispatch("chat/startChatRoom", {
				receiver: [this.activeChatUser],
				text: this.typedMessage,
			});
			this.activeChatKey = chatKey;
			this.typedMessage = "";
			this.searchQuery = "";
			this.activeTab = 0;
		},
		toggleIsChatPinned(value) {
			this.isChatPinned = value;
		},
		setSidebarWidth() {
			if (this.windowWidth < 1200) {
				this.isChatSidebarActive = this.clickNotClose = false;
			} else {
				this.isChatSidebarActive = this.clickNotClose = true;
			}
		},
		toggleChatSidebar(value = false) {
			if (!value && this.clickNotClose) return;
			this.isChatSidebarActive = value;
		},
		removeMemberFromList(id) {
			const foundMember = this.groupMembers.find((member) => member.id === id);
			if (foundMember) {
				const indexOf = this.groupMembers.indexOf(foundMember);
				this.groupMembers.splice(indexOf, 1);
			}
		},
		openConfirmDelete(member) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: this.$t("Chat.DeleteChatText"),
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
				accept: this.deleteChat,
			});
		},
		async deleteChat() {
			this.openLoading();
			await this.$store.dispatch("chat/deleteChatRoom", this.activeChatRoom);
			this.activeProfileSidebar = false;
			this.activeChatUser = 0;
			this.userProfileId = -1;
			this.typedMessage = "";
			this.activeChatRoom = new Chat({});
			this.activeChatKey = "";
			this.closeLoading();
		},
		async updateMembersEmit(members) {
			try {
				this.groupMembers = await this.$store.dispatch("chat/fetchContacts", {id: members});
			} catch (error) {
				console.error(error);
			}
		},
		isDefaultChat(key) {
			return key.split("_")[0] === "group";
		},
		senImageMessage(urlFile) {
			this.typedMessage = urlFile;
			this.sendMsg();
		},
	},
	watch: {
		activeTab(val) {
			this.$store.dispatch("chat/setChatSearchQuery", "");
			if (val > 0) {
				this.activeProfileSidebar = false;
				this.activeChatUser = null;
				this.userProfileId = -1;
				this.typedMessage = "";
				this.activeChatKey = "";
				this.isGroup = false;
			}
		},
		windowWidth() {
			this.setSidebarWidth();
		},
	},
	components: {
		VuePerfectScrollbar,
		ChatContact,
		UserProfile,
		ChatNavbar,
		ChatLog,
		ChatSettings,
		ChatImage,
	},
	created() {
		this.setSidebarWidth();
	},
	mounted() {
		this.$store.dispatch("chat/setChatSearchQuery", "");
	},
};
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/chat.scss";

.chat__bg {
	background-image: url("../../assets/images/pages/vuexy-login-bg.jpg");
	background-repeat: no-repeat;
	background-position: 50% 0;
	background-size: cover;
}

.scroll-area {
	margin: auto;
	width: 100%;
	height: 500px;
}

@media only screen and (min-width: 481px) {
	.scroll-area {
		margin: auto;
		width: 100%;
		height: 300px;
	}
}

@media only screen and (min-width: 768px) {
	.scroll-area {
		margin: auto;
		width: 100%;
		height: 350px;
	}
}

@media only screen and (min-width: 1289px) {
	.scroll-area {
		margin: auto;
		width: 100%;
		height: 500px;
	}
}

.emoji {
	font-size: large;
}

.w-custom {
	width: 26rem !important;
}
</style>
